'use es6';

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import compose from 'transmute/compose';
import { useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List, Range } from 'immutable';
import UIFlex from 'UIComponents/layout/UIFlex';
import { getLinkUse } from 'MeetingsBase/utils/color';
import DatePickerButton from 'MeetingsLib/components/schedule/date/DatePickerButton';
import ZeroState from 'MeetingsLib/components/schedule/ZeroState';
import { getEditLinkURL as getEditLinkURLSelector, getUserTimezone, getEvent as getEventSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAvailableDates as getAvailableDatesSelector } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { getCurrentTime } from 'MeetingsLib/selectors/RESTParamSelectors';
import { getConfiguredAvailabilityByDate, getTroubleshootTimesForSelectedDurationByDay, getConfiguredBookableDates, getMinimumNoticeTimeBusyDays, isTroubleshootingSelector } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import EnsureAvailableDate from 'MeetingsLib/components/schedule/date/EnsureAvailableDate';
import styled from 'styled-components';
import { HiddenAriaStatus } from 'MeetingsLib/components/decorative/HiddenAriaStatus';
import DatePickerHeader from './DatePickerHeader';
var StyledTable = styled.table.withConfig({
  displayName: "DatePicker__StyledTable",
  componentId: "yvieq2-0"
})(["table-layout:fixed;width:100%;margin:0;"]);
var StyledCaption = styled.caption.withConfig({
  displayName: "DatePicker__StyledCaption",
  componentId: "yvieq2-1"
})(["font-size:0;line-height:0;"]);
var StyledSkipNav = styled.a.withConfig({
  displayName: "DatePicker__StyledSkipNav",
  componentId: "yvieq2-2"
})(["position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden;"]);
var NUM_DAYS = 7;
var NUM_WEEKS = 5;
var DAY_RANGE = Range().take(NUM_DAYS).toArray();
var propTypes = {
  // passed
  branding: PropTypes.instanceOf(Branding).isRequired,
  compact: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile),
  setEventDate: PropTypes.func.isRequired,
  DatePickerMarker: PropTypes.func.isRequired,
  TimePickerMarker: PropTypes.func.isRequired
};

function DatePicker(_ref) {
  var branding = _ref.branding,
      compact = _ref.compact,
      profile = _ref.profile,
      setEventDate = _ref.setEventDate,
      DatePickerMarker = _ref.DatePickerMarker,
      TimePickerMarker = _ref.TimePickerMarker;
  var bookableDates = useSelector(function (state) {
    return getConfiguredBookableDates(state);
  });
  var configuredAvailabilityByDate = useSelector(function (state) {
    return getConfiguredAvailabilityByDate(state);
  });
  var currentTime = useSelector(function (state) {
    return getCurrentTime(state);
  });
  var availableDateOptions = useSelector(function (state) {
    return getAvailableDatesSelector(state);
  });
  var editLinkURL = useSelector(function (state) {
    return getEditLinkURLSelector(state);
  });
  var troubleshootTimesForSelectedDurationByDay = useSelector(function (state) {
    return getTroubleshootTimesForSelectedDurationByDay(state);
  });
  var isFetchingPublicAvailability = useSelector(function (state) {
    return getIsFetchingPublicAvailability(state);
  });
  var minimumNoticeTimeBusyDays = useSelector(function (state) {
    return getMinimumNoticeTimeBusyDays(state);
  });
  var isTroubleshooting = useSelector(function (state) {
    return isTroubleshootingSelector(state);
  });
  var userTimezone = useSelector(function (state) {
    return getUserTimezone(state);
  });
  var selectedDate = useSelector(function (state) {
    return getEventSelector(state).dateString;
  });

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      monthsAhead = _useState2[0],
      setMonthsAhead = _useState2[1];

  useEffect(function () {
    return setMonthsAhead(0);
  }, [isTroubleshooting, setMonthsAhead]);
  var minAvailableDay = I18n.moment(availableDateOptions.min());
  var maxAvailableDay = I18n.moment(availableDateOptions.max());
  var baseMonth = isTroubleshooting ? I18n.moment() : I18n.moment(availableDateOptions.min());
  var startOfMonth = I18n.moment(baseMonth).startOf('month').add(monthsAhead, 'months');
  var endOfMonth = I18n.moment(baseMonth).endOf('month').add(monthsAhead, 'months');
  var localeData = I18n.moment.localeData(I18n.locale) || I18n.moment.localeData(); // some locales start their week on Monday

  var daysToShiftForward = localeData._week.dow;

  function getDaysToShiftBack(dateObject) {
    var daysFromSunday = dateObject.isoWeekday() % 7;
    /*
     * if the month starts on Sunday and we are in a locale
     * that starts weeks on Monday, we would cut off the first
     * Sunday unless we rendered the entire previous week
     */

    if (daysFromSunday === 0 && daysToShiftForward === 1) {
      return 6;
    }

    return daysFromSunday - daysToShiftForward;
  }

  function getStartOfWeek(dateObject) {
    var daysToShiftBack = getDaysToShiftBack(dateObject);
    return I18n.moment(dateObject).subtract(daysToShiftBack, 'days');
  }

  var startOfWeek = getStartOfWeek(startOfMonth);
  var lastDisplayedDay = I18n.moment(startOfWeek).add({
    days: NUM_DAYS - 1,
    weeks: NUM_WEEKS - 1
  });
  var availabilityOutsideOfDisplayed = lastDisplayedDay.isBefore(minAvailableDay) || lastDisplayedDay.isBefore(maxAvailableDay);
  var minOrMaxAvailabilityNotDisplayed = lastDisplayedDay.isBefore(endOfMonth) && availabilityOutsideOfDisplayed;

  function handleSetEventDate(date) {
    if (isTroubleshooting || availableDateOptions.includes(date)) {
      setEventDate(date);
    }
  }

  var abbreviatedWeekdays = new List(localeData._weekdaysShort);
  var daysToShift = abbreviatedWeekdays.take(daysToShiftForward);
  var weekdayNames = abbreviatedWeekdays.splice(0, daysToShiftForward).concat(daysToShift);

  function getWeekRange(extraWeek) {
    var weeksToRender = extraWeek ? NUM_WEEKS + 1 : NUM_WEEKS;
    return Range().take(weeksToRender).toArray();
  }

  var renderDays = function renderDays(weekNumber) {
    return DAY_RANGE.map(function (daysToAdd) {
      var date = I18n.moment(startOfWeek).add({
        days: daysToAdd,
        weeks: weekNumber
      }).format('YYYY-MM-DD');
      return /*#__PURE__*/_jsx(DatePickerButton, {
        daysToAdd: daysToAdd,
        branding: branding,
        currentTime: currentTime,
        date: date,
        availableDates: availableDateOptions,
        handleSetEventDate: handleSetEventDate // troubleshoot mode
        ,
        bookableDates: bookableDates,
        configuredAvailabilityByDate: configuredAvailabilityByDate,
        editLinkURL: editLinkURL,
        troubleshootTimesForSelectedDurationByDay: troubleshootTimesForSelectedDurationByDay,
        minimumNoticeTimeBusyDays: minimumNoticeTimeBusyDays,
        isTroubleshooting: isTroubleshooting,
        userTimezone: userTimezone
      }, date + "-" + daysToAdd);
    });
  };

  function renderWeeksOrEmpty() {
    if (compact && !availableDateOptions.count() && !isFetchingPublicAvailability) {
      // Render empty state for compact view
      return /*#__PURE__*/_jsx("div", {
        className: "align-center m-top-12",
        children: /*#__PURE__*/_jsx(ZeroState, {
          profile: profile,
          linkUse: getLinkUse(branding.primaryColor),
          TimePickerMarker: TimePickerMarker
        })
      });
    }

    var currentMonth = startOfMonth.format('MMMM');
    var weekDaysClasses = "title-case " + (compact ? 'm-bottom-2' : 'm-bottom-4');
    var weeks = getWeekRange(minOrMaxAvailabilityNotDisplayed).map(function (weeksToAdd) {
      return /*#__PURE__*/_jsx("tr", {
        className: "extraWeek",
        children: renderDays(weeksToAdd)
      }, weeksToAdd);
    });
    return /*#__PURE__*/_jsxs(StyledTable, {
      children: [/*#__PURE__*/_jsx(StyledCaption, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.datePicker.caption",
          options: {
            currentMonth: currentMonth
          }
        })
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx("tr", {
          className: weekDaysClasses,
          children: weekdayNames.map(function (dayName) {
            return /*#__PURE__*/_jsx("th", {
              scope: "col",
              children: /*#__PURE__*/_jsx("span", {
                className: "is--heading-6",
                children: dayName
              })
            }, dayName);
          })
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: weeks
      })]
    });
  }

  var localizedDate = I18n.moment(selectedDate).format('MMMM Do');
  return /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    align: "center",
    className: "date-picker",
    children: [/*#__PURE__*/_jsx(DatePickerHeader, {
      startOfMonth: startOfMonth,
      availableDateOptions: availableDateOptions,
      compact: compact,
      isTroubleshooting: isTroubleshooting,
      setMonthsAhead: setMonthsAhead,
      branding: branding
    }), /*#__PURE__*/_jsx(StyledSkipNav, {
      onClick: function onClick() {
        document.getElementById('selected-date').focus();
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.skipToAvailableDate",
        options: {
          monthDay: localizedDate
        }
      })
    }), renderWeeksOrEmpty(), /*#__PURE__*/_jsx(DatePickerMarker, {}), /*#__PURE__*/_jsx(HiddenAriaStatus, {
      className: "calendar-status",
      role: "status",
      "aria-live": "polite",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "accessible.calendarStatus",
        options: {
          selectedDate: localizedDate
        }
      })
    })]
  });
}

DatePicker.propTypes = propTypes;
export default compose(EnsureAvailableDate, memo)(DatePicker);